/**
 * @file TextureResolutionPolicy.ts
 * @role Selects the startup texture-density preference from the canonical slot layout mode.
 * @owns The 1x-mobile / 2x-desktop asset-resolution policy.
 * @reads LayoutContext and the validated layout breakpoint policy.
 * @neverDoes Read browser globals, load assets, or change renderer resolution.
 * @invariants 14, 15.
 * @see docs/UI_LAYOUT_GUIDE.md, docs/wiki/Assets.md
 */
import type { LayoutPolicyConfig } from '@core/config/UiConfig';
import type { LayoutContext } from '@core/layout/LayoutContext';
import { LayoutMode, SlotLayoutModeResolver } from '@core/layout/SlotLayoutModeResolver';

export type TextureResolutionPreference = 1 | 2;

/**
 * Mobile-composition reels, including tablets, render within the 1x source contract;
 * desktop retains the 2x source for wide screens.
 */
export function textureResolutionForLayout(
  layout: LayoutContext,
  policy: Pick<
    LayoutPolicyConfig,
    'mobileBreakpoint' | 'tabletBreakpoint' | 'tabletLandscapeMaxAspectRatio'
  >,
): TextureResolutionPreference {
  return SlotLayoutModeResolver.resolve(layout, policy) === LayoutMode.Desktop ? 2 : 1;
}
